import React, { FC, useRef, useState, useMemo } from 'react';
import styled from 'styled-components';
import { ArrowContainer, Popover } from 'react-tiny-popover';
import { toast } from 'react-toastify';
import {
  useStoreSettingsApi,
  GeneralStoreSettingsInner,
} from '@/webapi/use-store-settings-api';
import { validateImageFile } from '@/utils/image-upload';
import { useMediaApi } from '@/webapi/use-media-api';
// @ts-ignore
import PencilIcon from '@/assets/pencil-brand-logo.svg';
import { breakpoints } from '@/components/responsive';

const LOGO_UPLOAD_TIMESTAMP_KEY = `logo_upload_timestamp`;
const SYNC_OPTION_VISIBILITY_DURATION_MS = 3 * 60 * 60 * 1000;

const getLocalStorageKey = (storeAlias: string) =>
  `${LOGO_UPLOAD_TIMESTAMP_KEY}_${storeAlias}`;

const saveLogoUploadTimestamp = (storeAlias: string) => {
  localStorage.setItem(getLocalStorageKey(storeAlias), Date.now().toString());
};

const clearLogoUploadTimestamp = (storeAlias: string) => {
  localStorage.removeItem(getLocalStorageKey(storeAlias));
};

const isWithinVisibilityDuration = (storeAlias: string): boolean => {
  const timestamp = localStorage.getItem(getLocalStorageKey(storeAlias));
  if (!timestamp) return false;
  const isWithinDuration =
    Date.now() - parseInt(timestamp, 10) < SYNC_OPTION_VISIBILITY_DURATION_MS;
  if (!isWithinDuration) {
    clearLogoUploadTimestamp(storeAlias);
  }
  return isWithinDuration;
};

interface LogoUploadDropdownProps {
  storeAlias: string;
  storeDomain: string;
  shopifyLogo?: string;
  uploadedShopLogo?: string;
  currentSettings?: GeneralStoreSettingsInner;
  onLogoUpdated: (logoUrl: string) => void;
  children: React.ReactNode;
}

export const LogoUploadDropdown: FC<LogoUploadDropdownProps> = ({
  storeAlias,
  storeDomain,
  shopifyLogo,
  uploadedShopLogo,
  currentSettings,
  onLogoUpdated,
  children,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { setGeneralStoreSettings } = useStoreSettingsApi();
  const { uploadGenericImage } = useMediaApi();

  const shouldShowSyncOption = useMemo(() => {
    if (!shopifyLogo || shopifyLogo.trim() === ``) return false;
    if (shopifyLogo === uploadedShopLogo) return false;
    if (!uploadedShopLogo) return true;
    return isWithinVisibilityDuration(storeAlias);
  }, [uploadedShopLogo, shopifyLogo, storeAlias]);

  const uploadButtonText = useMemo(() => {
    if (uploading) return `Uploading...`;
    if (uploadedShopLogo) return `Update Brand Logo`;
    return `Upload Brand Logo`;
  }, [uploading, uploadedShopLogo]);

  const uploadLogoToCDN = async (file: File): Promise<string> => {
    const resp = await uploadGenericImage(file);
    return resp.url;
  };

  const handleFileSelect = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const validation = validateImageFile(file);
    if (!validation.valid) {
      toast.error(validation.error);
      if (fileInputRef.current) {
        fileInputRef.current.value = ``;
      }
      return;
    }

    if (!currentSettings) {
      toast.error(`Something went wrong. Please try again.`);
      if (fileInputRef.current) {
        fileInputRef.current.value = ``;
      }
      return;
    }

    try {
      setUploading(true);

      const logoUrl = await uploadLogoToCDN(file);

      await setGeneralStoreSettings({
        ...currentSettings,
        uploaded_shop_logo: logoUrl,
      });

      onLogoUpdated(logoUrl);

      if (!uploadedShopLogo) {
        saveLogoUploadTimestamp(storeAlias);
      }

      toast.success(`Logo uploaded successfully!`);
      setIsOpen(false);
    } catch (error) {
      toast.error(`Failed to upload logo`);
      console.error(error);
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = ``;
      }
    }
  };

  const handleUploadClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    fileInputRef.current?.click();
  };

  const handleOpenSite = (e: React.MouseEvent) => {
    e.stopPropagation();
    window.open(`https://${storeDomain}`, `_blank`, `noopener,noreferrer`);
    setIsOpen(false);
  };

  const handleSyncShopifyLogo = async (e: React.MouseEvent) => {
    e.stopPropagation();

    if (!shopifyLogo) return;

    if (!currentSettings) {
      toast.error(`Something went wrong. Please try again.`);
      return;
    }

    try {
      setSyncing(true);

      await setGeneralStoreSettings({
        ...currentSettings,
        uploaded_shop_logo: shopifyLogo,
      });

      onLogoUpdated(shopifyLogo);

      toast.success(`Logo synced from Shopify successfully!`);
      setIsOpen(false);
    } catch (error) {
      toast.error(`Failed to sync logo from Shopify`);
      console.error(error);
    } finally {
      setSyncing(false);
    }
  };

  const handleRemoveLogo = async (e: React.MouseEvent) => {
    e.stopPropagation();

    if (!currentSettings) {
      toast.error(`Something went wrong. Please try again.`);
      return;
    }

    try {
      await setGeneralStoreSettings({
        ...currentSettings,
        uploaded_shop_logo: ``,
      });

      onLogoUpdated(``);
      clearLogoUploadTimestamp(storeAlias);

      toast.success(`Logo removed successfully!`);
      setIsOpen(false);
    } catch (error) {
      toast.error(`Failed to remove logo`);
      console.error(error);
    }
  };

  const handleTogglePopover = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsOpen(!isOpen);
  };

  return (
    <>
      <Wrapper>
        <Popover
          isOpen={isOpen}
          positions={[`bottom`]}
          onClickOutside={() => setIsOpen(false)}
          containerStyle={{ zIndex: `1000` }}
          padding={5}
          content={({ position, childRect, popoverRect }) => (
            <ArrowContainer
              position={position}
              childRect={childRect}
              popoverRect={popoverRect}
              arrowColor="white"
              arrowSize={10}
              arrowStyle={{
                marginTop: `2px`,
                filter: `drop-shadow(#e7e7e7 0px -1px 0px)`,
              }}
            >
              <Dropdown>
                <DropdownItem onClick={handleOpenSite}>
                  <span>Open Site</span>
                </DropdownItem>

                <DropdownItem onClick={handleUploadClick} disabled={uploading}>
                  <span>{uploadButtonText}</span>
                </DropdownItem>

                {shouldShowSyncOption && (
                  <DropdownItem
                    onClick={handleSyncShopifyLogo}
                    disabled={syncing}
                  >
                    <span>
                      {syncing ? `Syncing...` : `Sync Logo From Shopify`}
                    </span>
                  </DropdownItem>
                )}

                {uploadedShopLogo && (
                  <DropdownItem onClick={handleRemoveLogo}>
                    <span>Remove Logo</span>
                  </DropdownItem>
                )}
              </Dropdown>
            </ArrowContainer>
          )}
        >
          <LogoContainer onClick={handleTogglePopover}>
            {children}
          </LogoContainer>
        </Popover>

        <PencilButton
          $isOpen={isOpen}
          aria-expanded={isOpen}
          onClick={handleTogglePopover}
          title="Edit logo"
        >
          <img src={PencilIcon} alt="Edit" />
        </PencilButton>
      </Wrapper>

      <HiddenFileInput
        ref={fileInputRef}
        type="file"
        accept="image/svg+xml,image/png,image/jpeg,image/jpg,image/webp"
        onChange={handleFileSelect}
      />
    </>
  );
};

const Wrapper = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 0.8rem;

  ${breakpoints.down(`md`)} {
    width: 100%;
  }

  &:hover {
    button {
      display: flex;
    }
  }

  button[aria-expanded='true'] {
    display: flex;
  }
`;

const LogoContainer = styled.div`
  display: flex;
  align-items: center;
  min-height: 4rem;
  max-height: 4rem;
  cursor: pointer;
  transition: opacity 0.2s ease;

  ${breakpoints.down(`md`)} {
    width: 100%;
    justify-content: center;
  }

  &:hover {
    opacity: 0.8;
  }

  img {
    max-height: 4rem;
    max-width: 24rem;
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  span {
    white-space: nowrap;
  }
`;

const PencilButton = styled.button<{ $isOpen: boolean }>`
  display: ${(props) => (props.$isOpen ? `flex` : `none`)};
  align-items: center;
  justify-content: center;
  width: 2.4rem;
  height: 2.4rem;
  border-radius: 0.4rem;
  cursor: pointer;

  img {
    width: 1.6rem;
    height: 1.6rem;
    display: block;
  }
`;

const Dropdown = styled.div`
  min-width: 20rem;
  padding: 0.8rem 0;
  background: white;
  border-radius: 0.8rem;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  border: 1px solid #e7e7e7;
`;

const DropdownItem = styled.button<{ disabled?: boolean }>`
  display: flex;
  align-items: center;
  gap: 1rem;
  width: 100%;
  padding: 1rem 1.6rem;
  border: none;
  background: transparent;
  font-size: 1.4rem;
  font-family: 'Inter', serif;
  text-align: left;
  cursor: pointer;
  transition: background 0.2s ease;
  color: ${(props) => (props.disabled ? `#999` : `#000`)};

  &:hover:not(:disabled) {
    background: #fafafa;
  }

  &:disabled {
    cursor: not-allowed;
  }

  svg {
    flex-shrink: 0;
  }
`;

const HiddenFileInput = styled.input`
  display: none;
`;
